import type { Locale } from "@adpilot/shared";
import { HTTPError } from "ky";
import { api } from "@/lib/api";
import { persistLocale } from "@/lib/locale-api";
import type { Shop, User } from "@/stores/auth";
import { useAuthStore } from "@/stores/auth";
import { useLocaleStore } from "@/stores/locale";

export interface AuthUserPayload extends Omit<User, "isMainAccount" | "modulePermissions"> {
  locale: Locale | null;
  shops: Shop[];
  // ADP-167: optional so older payloads hydrate safely — hydrate defaults the
  // pair to false/null (no main-account privileges, no module restriction).
  isMainAccount?: boolean;
  modulePermissions?: string[] | null;
}

export function hydrateAuthUser(payload: AuthUserPayload): void {
  const { shops, locale, isMainAccount, modulePermissions, ...user } = payload;
  useAuthStore.getState().setShops(shops);
  useAuthStore.getState().setUser({
    ...user,
    isMainAccount: isMainAccount ?? false,
    modulePermissions: modulePermissions ?? null,
  });

  if (locale) {
    useLocaleStore.getState().setLocale(locale);
  } else {
    void persistLocale(useLocaleStore.getState().locale).catch(() => undefined);
  }
}

export async function fetchAuthUser(): Promise<AuthUserPayload> {
  const response = await api
    .get("auth/me")
    .json<{ success: true; data: { user: AuthUserPayload } }>();
  return response.data.user;
}

/** Probe the cookie session from a public route without redirecting anonymous users. */
export async function fetchOptionalAuthUser(): Promise<AuthUserPayload | null> {
  try {
    const response = await api
      .get("auth/me", { context: { suppressAuthRedirect: true } })
      .json<{ success: true; data: { user: AuthUserPayload } }>();
    return response.data.user;
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) return null;
    throw error;
  }
}
